/** Tiny, XSS-safe markdown renderer for job-ad bodies.
 *
 * The source is escaped BEFORE any conversion, so raw HTML in the markdown
 * can never reach the DOM (same safety property as the app's
 * MarkdownDocContent, which deliberately omits rehype-raw). Supports the
 * subset the ad generator produces: ##/### headings, paragraphs, -/* and
 * 1. lists, **bold**, *italic*, [text](https://...) links, video embeds and
 * images — whether the image is alone on its line or sits inside a bullet.
 */

import { imageEmbedHtml } from "./image-embed";
import { matchVideoLine, videoEmbedHtml } from "./video-embed";

function escapeHtml(text: string): string {
	return text
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;");
}

function inline(text: string): string {
	return (
		text
			.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
			.replace(/\*([^*]+)\*/g, "<em>$1</em>")
			// Before links, and not only whole-line images: a picture dropped
			// into a bullet or mid-paragraph sits inside that text, and left to
			// the link rule below it would reach the candidate as the literal
			// "![](…)" they were never meant to see.
			.replace(
				/!\[([^\]]*)\]\(([^)\s]+)\)/g,
				(whole, alt: string, src: string) => {
					return imageEmbedHtml(alt, src) ?? whole;
				}
			)
			.replace(
				/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
				'<a href="$2" rel="noopener noreferrer" target="_blank">$1</a>'
			)
	);
}

export function renderMarkdown(source: string): string {
	const lines = escapeHtml(source.trim()).split("\n");
	const html: string[] = [];
	let paragraph: string[] = [];
	let bullets: string[] = [];
	let numbers: string[] = [];
	const flushParagraph = (): void => {
		if (paragraph.length) {
			html.push(`<p>${inline(paragraph.join("<br>"))}</p>`);
			paragraph = [];
		}
	};
	const flushBullets = (): void => {
		if (bullets.length) {
			const items = bullets.map((item) => `<li>${inline(item)}</li>`).join("");
			html.push(`<ul>${items}</ul>`);
			bullets = [];
		}
	};
	// The editor produces real <ol>s, so the public page has to as well —
	// otherwise a numbered list shows here as literal "1. " text.
	const flushNumbers = (): void => {
		if (numbers.length) {
			const items = numbers.map((item) => `<li>${inline(item)}</li>`).join("");
			html.push(`<ol>${items}</ol>`);
			numbers = [];
		}
	};
	for (const raw of lines) {
		const line = raw.trim();
		if (line === "") {
			flushParagraph();
			flushBullets();
			flushNumbers();
			continue;
		}
		const image = line.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
		if (image) {
			const rendered = imageEmbedHtml(image[1], image[2]);
			if (rendered) {
				flushParagraph();
				flushBullets();
				flushNumbers();
				html.push(rendered);
				continue;
			}
		}
		const video = matchVideoLine(line);
		if (video) {
			flushParagraph();
			flushBullets();
			flushNumbers();
			html.push(videoEmbedHtml(video));
			continue;
		}
		// Headings count even mid-block — the ad generator writes
		// "## Heading\ntext" without blank lines. # is demoted to h2 (the
		// page title is the only h1), depth capped at h4.
		const heading = line.match(/^(#{1,4}) (.*)$/);
		if (heading) {
			flushParagraph();
			flushBullets();
			flushNumbers();
			const level = Math.min(4, Math.max(2, heading[1].length));
			html.push(`<h${level}>${inline(heading[2])}</h${level}>`);
			continue;
		}
		if (/^[-*] /.test(line)) {
			flushParagraph();
			flushNumbers();
			bullets.push(line.replace(/^[-*] /, ""));
			continue;
		}
		if (/^\d+[.)] /.test(line)) {
			flushParagraph();
			flushBullets();
			numbers.push(line.replace(/^\d+[.)] /, ""));
			continue;
		}
		flushBullets();
		flushNumbers();
		paragraph.push(line);
	}
	flushParagraph();
	flushBullets();
	flushNumbers();
	return html.join("\n");
}
